import React, { useEffect, useState } from 'react';
import './PreApprovalForm.scss';
import { TextInput, InputAutocompleteAddress } from '../../atoms/TextInput/TextInput';
import PasswordInput from '../../atoms/TextInput/PasswordInput';
import { FooterContent } from '../ResumeFooter/ResumeFooter';
import { OptionDropdownInner } from '../../atoms/OptionDropdown/OptionDropdown';
import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { SPECIAL_CHARACTERS_REGEX } from '../../../utils/constants';
import { SendLoanFormSubmitted } from '../SendLoanFormSubmitted/SendLoanFormSubmitted';
import { sendLeadData } from '../SendLoanForm/SendLoanForm';
import { ONBOARDING_STEPS } from '../../../utils/constants';
import { OptionDropdown } from '../../atoms/OptionDropdown/OptionDropdown';
import { useOwnership } from '../../../context/OwnershipContext';
import DropdownOwnerships from '../../atoms/DropdownOwnerships/DropdownOwnerships';
import { useCheckBorrowerEmailExists } from '../../../services/borrowers';
import { useReactiveVar } from '@apollo/client';
import { loggedUserTypeVar } from '../../../App';

/* Utility Functions */
// Function to format phone number
export const formatPhoneNumber = (phoneNumber) => {
  // Remove all non-digit characters
  let digits = phoneNumber.replace(/\D/g, '');

  // Limit to 10 digits
  digits = digits.substring(0, 10);

  // Format as (123) 456-7890
  const formatted = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;

  return formatted;
}

// Function to validate phone number
export const validatePhoneNumber = (phoneNumber) => {
  const digits = phoneNumber.replace(/\D/g, '');
  if (digits.length === 0) return "Phone number is required";
  if (digits.length < 10) return "Phone number must have 10 digits";
  return null;
}

/* Spinners */
const CustomSpinner = ({ color }) => (
  <Spin
    indicator={
      <LoadingOutlined
        style={{
          fontSize: 14,
          color: color,
          position: "relative",
          bottom: "1px"
        }}
        spin
      />
    }
  />
);

const WhiteSpinner = () => <CustomSpinner color="white" />;
const BlueSpinner = () => <CustomSpinner color="#162E43" />;

/* Components */
export const PreApprovalForm = (props) => {
  const { ownershipSelected } = useOwnership();
  const isSomeoneElse = ownershipSelected?.isNew === true;
  const { values, onContinue, borrowerData, ownerships, isBrokerMode, setValue, setSelectedIndex } = props;

  const isProcessing = Boolean(borrowerData)
  const hidePasswordFields = isProcessing;

  const [firstName, setFirstName] = useState(ownershipSelected?.name || "")
  const [lastName, setLastName] = useState(ownershipSelected?.lastname || "")
  const [email, setEmail] = useState(isSomeoneElse ? borrowerData?.email : ownershipSelected?.email || "")
  const [phoneNumber, setPhoneNumber] = useState(isSomeoneElse ? borrowerData?.phone : ownershipSelected?.phone || "")
  const [password, setPassword] = useState(hidePasswordFields ? "password" : "")
  const [passwordConfirmation, setPasswordConfirmation] = useState(hidePasswordFields ? "password" : "")
  const [investmentAddress, setInvestmentAddress] = useState("")

  const [isLoading, setIsLoading] = useState(false)
  const [emailValidationLoading, setEmailValidationLoading] = useState(false)

  const [errors, setErrors] = useState({});

  // Email validation hook
  const { checkEmail, emailExists, loading: emailCheckLoading } = useCheckBorrowerEmailExists();
  
  // Reset email validation when email changes
  const [emailExistsState, setEmailExistsState] = useState(false);
  const [addressSelected, setAddressSelected] = useState(false)

  const shouldHidePasswordFields = hidePasswordFields || addressSelected;

  const userData = {
    firstName,
    lastName,
    email,
    phoneNumber,
    password,
    passwordConfirmation,
    investmentAddress
  }

  useEffect(() => {
    if (ownershipSelected) {
      setFirstName(isSomeoneElse ? '' : ownershipSelected.name || "");
      setLastName(isSomeoneElse ? '' : ownershipSelected.lastname || "");
      setEmail(isSomeoneElse ? borrowerData?.email : ownershipSelected.email || "");
      setPhoneNumber(isSomeoneElse ? borrowerData?.phone : ownershipSelected.phone || "");
    }
  }, [ownershipSelected]);

  const isFormComplete = () => {
    return Object.values(userData).every(value => value !== undefined && value !== '');
  }

  // Password validation functions
  const validatePasswordRequirements = (password) => {
    const requirements = [
      { label: "Lowercase letter", validator: (pwd) => /[a-z]/.test(pwd) },
      { label: "Uppercase letter", validator: (pwd) => /[A-Z]/.test(pwd) },
      { label: "Symbol", validator: (pwd) => SPECIAL_CHARACTERS_REGEX.test(pwd) },
      { label: "8 characters", validator: (pwd) => pwd.length >= 8 }
    ];

    return requirements.map(req => ({
      ...req,
      met: req.validator(password)
    }));
  };

  const isPasswordValid = (password) => {
    const requirements = validatePasswordRequirements(password);
    return requirements.every(req => req.met);
  };

  // Function to validate email existence
  const validateEmailExistence = async (emailValue) => {
    if (!emailValue || !/\S+@\S+\.\S+/.test(emailValue)) {
      setEmailExistsState(false);
      return null; // Don't check if email is invalid
    }
    
    setEmailValidationLoading(true);
    try {
      const result = await checkEmail({ variables: { email: emailValue } });
      if (result.data) {
        const emailExists = result.data.checkBorrowerEmailExists;
        setEmailExistsState(emailExists);
      }
    } catch (error) {
    } finally {
      setEmailValidationLoading(false);
    }
  };

  const validateInput = (label, value) => {
    switch (label) {
      case "First name":
      case "Last name":
        return value.trim() === "" ? "This field is required" : null;
      case "Investment Address":
        return value.trim() === "" ? "We need this to generate your pre-approval letter and term sheet" : null;
      case "Email address":
        const emailRegex = /\S+@\S+\.\S+/;
        if (!emailRegex.test(value)) return "Invalid email address";
        if (emailExistsState && !emailValidationLoading) return "This email is already registered";
        return null;
      case "Phone number":
        if (value.trim() === "") return "This field is required";
        const digits = value.replace(/\D/g, '');
        if (digits.length < 10) return "Phone number must have 10 digits";
        return null;
      case "Password":
        // Skip password validation if fields are hidden (user is logged in)
        if (shouldHidePasswordFields) return null;
        if (value.length === 0) return "Password is required";
        if (!isPasswordValid(value)) return "Password does not meet requirements";
        return null;
      case "Confirm Password":
        // Skip password confirmation validation if fields are hidden (user is logged in)
        if (shouldHidePasswordFields) return null;
        if (!password || !value) return "Please confirm your password";
        if (value !== password) return "Passwords do not match";
        return null;
      default:
        return null;
    }
  };

  const checkErrors = () => {
    const newErrors = {
      firstName: validateInput("First name", firstName),
      lastName: validateInput("Last name", lastName),
      email: validateInput("Email address", email),
      phoneNumber: validateInput("Phone number", phoneNumber),
      password: shouldHidePasswordFields ? null : validateInput("Password", password),
      passwordConfirmation: shouldHidePasswordFields ? null : validateInput("Confirm Password", passwordConfirmation),
      investmentAddress: validateInput("Investment Address", investmentAddress),
    };
    setErrors(newErrors);
    return newErrors
  }

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      checkErrors();
    }
  }, [firstName, lastName, email, phoneNumber, ...(hidePasswordFields ? [] : [password, passwordConfirmation]), investmentAddress, emailExistsState, emailValidationLoading])
  
  // Validate email existence when email changes
  useEffect(() => {
    if (email && !borrowerData) {
      const timeoutId = setTimeout(() => {
        validateEmailExistence(email);
      }, 1000); // Debounce for 1 second

      return () => clearTimeout(timeoutId);
    } else {
      // Clear email validation state when email is empty or borrowerData exists
      setEmailValidationLoading(false);
      setEmailExistsState(false);
    }
  }, [email, borrowerData]);
  

  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const handleContinue = (isKeepMeInTheLoop) => {

    const errors = checkErrors();
    const hasErrors = Object.values(errors).some(error => error !== null);

    if (!investmentAddress) {
      setAddressSelected(true)
    }

    if (isKeepMeInTheLoop === true) {
      // If address is selected (I don't have specific property in mind), exclude password fields from validation
      if (addressSelected) {
        const { investmentAddress, password, passwordConfirmation, ...errs } = errors
        const { investmentAddress: _, password: __, passwordConfirmation: ___, ...ud } = userData

        const isFormComplete = Object.values(ud).every(value => value !== undefined && value !== '');
        const hasErrors = Object.values(errs).some(error => error !== null);

        if (isFormComplete && !hasErrors) {
          const { password, passwordConfirmation, ...leadData } = userData
          sendLeadData({
            name: `${leadData['firstName']} ${leadData['lastName']}`,
            ...leadData,
            ...values
          })
          setShowConfirmationModal(true)
        }
      } else {
        // Original logic for when address is not selected
        const { investmentAddress, ...errs } = errors
        const { investmentAddress: _, ...ud } = userData

        const isFormComplete = Object.values(ud).every(value => value !== undefined && value !== '');
        const hasErrors = Object.values(errs).some(error => error !== null);

        if (isFormComplete && !hasErrors) {
          const { password, passwordConfirmation, ...leadData } = userData
          sendLeadData({
            name: `${leadData['firstName']} ${leadData['lastName']}`,
            ...leadData,
            ...values
          })
          setShowConfirmationModal(true)
        }
      }

      return
    }

    // If address is selected (I don't have specific property in mind), skip password validation
    if (addressSelected) {
      const { password, passwordConfirmation, investmentAddress, ...errs } = errors;
      const { password: _, passwordConfirmation: __, investmentAddress: ___, ...ud } = userData;

      const isFormComplete = Object.values(ud).every(value => value !== undefined && value !== '');
      const hasErrors = Object.values(errs).some(error => error !== null);

      if (isFormComplete && !hasErrors) {
        setIsLoading(true)
        setTimeout(() => {
          setIsLoading(false)
          onContinue({ ...userData, password: "", passwordConfirmation: "" });
        }, 1000)
      }
      return;
    }

    if (isFormComplete() && !hasErrors) {

      setIsLoading(true)
      setTimeout(() => {
        setIsLoading(false)
        onContinue(userData);
      }, 1000)
    }
  }

  const hasErrors = Object.values(errors).some(error => error !== null);

  const options = [
    {
      value: "the borrower",
      label: "the borrower",
      disabled: false,
      className: undefined
    },
    {
      value: "a broker",
      label: "a broker",
      disabled: false,
      className: undefined
    },
    {
      value: "an agent/attorney",
      label: "an agent/attorney",
      disabled: false,
      className: undefined
    },
    {
      value: "someone else",
      label: "someone else",
      disabled: false,
      className: undefined
    },
  ]

  const w = (f) => (a) => { if (!borrowerData || !ownershipSelected || isSomeoneElse) f(a) }

  const title = borrowerData ? "Confirm a few details to get your term sheet and pre-approval letter" : "Enter your info to get a term sheet and pre-approval letter"
  const button = "Submit Application"

  useEffect(() => {
    if (investmentAddress) {
      setAddressSelected(false)
    }
  }, [investmentAddress])

  if (showConfirmationModal) {
    return (
      <SendLoanFormSubmitted isWhite={true} />
    )
  }

  const Wrapper = (
    (props) => <OptionDropdown {...props} isBrokerMode={isBrokerMode} values={values} setValue={setValue} setSelectedIndex={setSelectedIndex} parentClassName={`option-custom-${props.index}`} />
  )

  const ficoRange = (
    <Wrapper
      isSet={values[ONBOARDING_STEPS[5].text]}
      index={5}
    />
  )

  const propertiesNegotiated = (
    <Wrapper
      isSet={values[ONBOARDING_STEPS[6].text]}
      index={6}
    />
  )

  return (
    <div className='pre-approval-form'>
      <div className='first-section'>
        <h1>{title}</h1>
        {borrowerData &&
          <div className='text-borrower'>
            <DropdownOwnerships
              items={ownerships}
            />
            {' '}is the borrower
          </div>
        }
        <div className='inputs-container'>
          <TextInput label={"First name"} value={firstName} onChange={w(setFirstName)} error={errors.firstName} />
          <TextInput label={"Last name"} value={lastName} onChange={w(setLastName)} error={errors.lastName} />
          {!borrowerData && (
            <React.Fragment>
              <TextInput 
                label={"Email address"} 
                value={email} 
                onChange={w(setEmail)} 
                error={errors.email} 
                loading={emailValidationLoading || emailCheckLoading}
              />
              <TextInput label={"Phone number"} value={phoneNumber} onChange={w((e) => setPhoneNumber(formatPhoneNumber(e)))} error={errors.phoneNumber} />
            </React.Fragment>
          )}
          {borrowerData && (
            <React.Fragment>
              <div className='custom-select custom-select-fico'>{ficoRange}</div>
              <div className='custom-select custom-select-properties'>{propertiesNegotiated}</div>
            </React.Fragment>
          )}
          {
            shouldHidePasswordFields ?
              null
              :
              <React.Fragment>
                <PasswordInput
                  label={"Password"}
                  value={password}
                  onChange={(e) => setPassword(e)}
                  error={errors.password}
                  showRequirements={true}
                  wrapperClassName="requirements-desktop"
                />
                <PasswordInput
                  label={"Confirm Password"}
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e)}
                  error={errors.passwordConfirmation}
                  showRequirements={true}
                  requirementsValue={password}
                  wrapperClassName="requirements-mobile"
                />
              </React.Fragment>
          }
        </div>

        <div className='borrower'>
          {
            borrowerData ?
              null
              :
              <p>I am <OptionDropdownInner defaultValue={options[0]} options={options} customWidth={"200px"} /></p>
          }
        </div>

        <div className='property'>
          <TextInput error={errors.investmentAddress}>
            <InputAutocompleteAddress
              value={investmentAddress}
              onSelect={({ formatted_address }) => {
                const addressWithoutCountry = formatted_address.replace(/, [^,]*$/, "");
                setInvestmentAddress(addressWithoutCountry);
              }}
            />
          </TextInput>
          {!borrowerData && <AddressCaptionInner {...{ addressSelected, setAddressSelected, setInvestmentAddress }} />}
        </div>
        {
          !addressSelected ?
            <div className='resume-container'>
              <FooterContent values={values} oneLine={true} isSpan={true} firstPerson />
            </div>
            :
            null
        }

        <div>
          {
            !addressSelected || borrowerData ?
              <Button isLoading={isLoading} disabled={hasErrors} onClick={handleContinue}>
                {button}
              </Button>
              :
              <>
                {
                  !investmentAddress ?
                    <Button outlined isLoading={isLoading} onClick={() => handleContinue(true)}>
                      Keep me in the loop
                    </Button>
                    :
                    <Button isLoading={isLoading} disabled={hasErrors} onClick={handleContinue}>
                      {button}
                    </Button>
                }
              </>
          }
          <span className='caption'>
            We will NEVER sell your info.<br></br>
            By providing your phone and email, you agree to receive text messages from Constitution Lending.
          </span>
        </div>

      </div>
    </div>
  );
}

export const AddressCaptionInner = (props) => {
  const {
    addressSelected,
    setAddressSelected,
    setInvestmentAddress,
    caption = "I don't have specific property in mind"
  } = props;
  const addressCopy = "We need a property address to give you a loan, but if you're considering options, one of our loan officers would love to talk to you anyway"
  
  const loggedUserType = useReactiveVar(loggedUserTypeVar)
  const isUserLoggedIn = loggedUserType === "Admin" || loggedUserType === "Borrower" || loggedUserType === "Broker"

  const handleOnClick = () => {
    setInvestmentAddress("")
    setAddressSelected(!addressSelected)
  }
  
  // Hide the "I don't have specific property in mind" option if user is logged in
  if (isUserLoggedIn) {
    return null
  }
  
  return (
    <React.Fragment>
      <p
        className={addressSelected ? 'address-short-caption selected' : 'address-short-caption'}
        onClick={handleOnClick}>
        {caption}
      </p>
      {
        addressSelected ?
          <p className='address-caption'>{addressCopy}</p>
          :
          null
      }
    </React.Fragment>
  )
}

export const AddressCaption = () => {
  const [addressSelected, setAddressSelected] = useState(false)

  return (
    <AddressCaptionInner {...{ addressSelected, setAddressSelected }} />
  )
}

export const Button = ({ children, onClick, disabled, isLoading, outlined = false }) => (
  <div 
    className={`loan-options-button ${outlined ? 'outlined' : ''} ${disabled ? 'disabled' : ''}`} 
    onClick={disabled ? undefined : onClick}
  >
    {children}
    {
      isLoading && (
        <span style={{ marginLeft: '8px' }}>
          {outlined ? <BlueSpinner /> : <WhiteSpinner />}
        </span>
      )
    }
  </div>
);